'use client';

import { useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '@/components/ui/AppIcon';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

// Tech Data
const techCategories = [
    {
        title: 'Frontend Magic',
        icon: 'CommandLineIcon',
        color: 'cyan',
        description: 'Pixel-perfect, responsive interfaces.',
        stack: ['React', 'Next.js', 'TypeScript', 'Tailwind', 'Three.js', 'Framer Motion']
    },
    {
        title: 'Backend Core',
        icon: 'ServerIcon',
        color: 'indigo',
        description: 'Scalable, secure, and high-performance APIs.',
        stack: ['Node.js', 'Python', 'Go', 'PostgreSQL', 'Redis', 'GraphQL']
    },
    {
        title: 'Cloud & AI',
        icon: 'CloudIcon',
        color: 'violet',
        description: 'Next-gen infrastructure and intelligence.',
        stack: ['AWS', 'Docker', 'Kubernetes', 'OpenAI API', 'TensorFlow', 'Vercel']
    },
    {
        title: 'Mobile & Cross',
        icon: 'DevicePhoneMobileIcon',
        color: 'fuchsia',
        description: 'Native-grade experiences on every device.',
        stack: ['React Native', 'Flutter', 'Expo', 'Swift', 'Kotlin']
    }
];

const TechCard = ({ tech, index }: { tech: any; index: number }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const glowRef = useRef<HTMLDivElement>(null);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current || !glowRef.current) return;

        const rect = cardRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const rotateX = ((y - centerY) / centerY) * -10; // Max 10deg rotation
        const rotateY = ((x - centerX) / centerX) * 10;

        gsap.to(cardRef.current, {
            rotateX: rotateX,
            rotateY: rotateY,
            duration: 0.4,
            ease: 'power2.out',
            transformPerspective: 1000
        });

        gsap.to(glowRef.current, {
            x: x - rect.width / 2,
            y: y - rect.height / 2,
            opacity: 1,
            duration: 0.2
        });
    };

    const handleMouseLeave = () => {
        if (!cardRef.current || !glowRef.current) return;

        gsap.to(cardRef.current, {
            rotateX: 0,
            rotateY: 0,
            duration: 0.7,
            ease: 'elastic.out(1, 0.5)'
        });

        gsap.to(glowRef.current, {
            opacity: 0,
            duration: 0.5
        });
    };

    return (
        <div
            ref={cardRef}
            className="group relative p-8 rounded-[2.5rem] bg-white border border-slate-100 shadow-xl overflow-hidden cursor-pointer h-full will-change-transform"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ transformStyle: 'preserve-3d' }}
        >
            {/* Holographic Glow */}
            <div
                ref={glowRef}
                className={`absolute w-[500px] h-[500px] bg-gradient-to-r from-${tech.color}-500/20 to-transparent rounded-full blur-3xl pointer-events-none -translate-x-1/2 -translate-y-1/2 opacity-0 z-0`}
                style={{ left: '50%', top: '50%' }}
            />

            {/* Content Container (Lifted in 3D) */}
            <div className="relative z-10 flex flex-col h-full" style={{ transform: 'translateZ(30px)' }}>
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-2xl bg-${tech.color}-50 flex items-center justify-center shadow-inner`}>
                        <Icon name={tech.icon} className={`w-7 h-7 text-${tech.color}-600`} />
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-slate-900 leading-tight">{tech.title}</h3>
                        <div className={`mt-1 h-1 w-12 rounded-full bg-${tech.color}-500`} />
                    </div>
                </div>

                <p className="text-slate-600 mb-8 text-sm leading-relaxed font-medium">
                    {tech.description}
                </p>

                {/* Tech Tags Grid */}
                <div className="grid grid-cols-2 gap-3 mt-auto">
                    {tech.stack.map((item: string, i: number) => (
                        <div
                            key={i}
                            className="flex items-center gap-2 text-xs font-bold text-slate-600 bg-slate-50 px-3 py-2 rounded-xl border border-slate-100/50 group-hover:border-${tech.color}-200/50 transition-colors"
                        >
                            <div className={`w-1.5 h-1.5 rounded-full bg-${tech.color}-400`} />
                            {item}
                        </div>
                    ))}
                </div>
            </div>

            {/* Decorative Background Pattern */}
            <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none"
                style={{
                    backgroundImage: 'radial-gradient(circle at 1px 1px, #64748b 1px, transparent 0)',
                    backgroundSize: '24px 24px'
                }}
            />
        </div>
    );
};

const TechStreams = () => (
    <div className="absolute inset-x-0 top-0 bottom-0 pointer-events-none z-0">
        {/* Desktop Streams (Horizontal) */}
        <div className="hidden md:block w-full h-full">
            <svg className="w-full h-full" viewBox="0 0 1200 800" fill="none" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="stream-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(99, 102, 241, 0)" />
                        <stop offset="50%" stopColor="rgba(99, 102, 241, 0.5)" />
                        <stop offset="100%" stopColor="rgba(168, 85, 247, 0)" />
                    </linearGradient>
                </defs>

                {/* Connection Lines */}
                <path d="M150,200 C300,200 300,600 450,600" className="stroke-indigo-200/20 stroke-2 fill-none" />
                <path d="M450,600 C600,600 600,300 750,300" className="stroke-violet-200/20 stroke-2 fill-none" />
                <path d="M750,300 C900,300 900,500 1050,500" className="stroke-fuchsia-200/20 stroke-2 fill-none" />

                {/* Moving Signals */}
                <circle r="4" fill="#818cf8" className="moving-signal opacity-0">
                    <animateMotion dur="4s" repeatCount="indefinite" path="M150,200 C300,200 300,600 450,600" keyPoints="0;1" keyTimes="0;1" />
                </circle>
                <circle r="4" fill="#a78bfa" className="moving-signal opacity-0" style={{ animationDelay: '1s' }}>
                    <animateMotion dur="5s" begin="1s" repeatCount="indefinite" path="M450,600 C600,600 600,300 750,300" keyPoints="0;1" keyTimes="0;1" />
                </circle>
                <circle r="4" fill="#e879f9" className="moving-signal opacity-0" style={{ animationDelay: '2s' }}>
                    <animateMotion dur="4.5s" begin="2s" repeatCount="indefinite" path="M750,300 C900,300 900,500 1050,500" keyPoints="0;1" keyTimes="0;1" />
                </circle>
            </svg>
        </div>


    </div>
);

const TechStackModern = () => {
    const containerRef = useRef<HTMLElement>(null);

    useGSAP(() => {
        // Staggered Reveal
        gsap.from('.tech-card-wrapper', {
            y: 100,
            opacity: 0,
            duration: 1,
            stagger: 0.15,
            ease: 'power4.out',
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top 70%'
            }
        });

        // Background Elements Parallax
        gsap.to('.tech-bg-float', {
            y: -100,
            ease: 'none',
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1
            }
        });

        // Enhance signal visibility on scroll
        gsap.to('.moving-signal', {
            opacity: 1,
            duration: 1,
            stagger: 0.5,
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top 60%'
            }
        });

    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="py-32 relative bg-slate-50/80 overflow-hidden">
            <TechStreams />

            {/* Background 3D Elements */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -left-[10%] top-[20%] w-[600px] h-[600px] bg-indigo-200/20 rounded-full blur-[100px] tech-bg-float" />
                <div className="absolute -right-[10%] bottom-[10%] w-[500px] h-[500px] bg-violet-200/20 rounded-full blur-[100px] tech-bg-float" data-speed="0.5" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center mb-20">
                    <span className="inline-block py-1 px-3 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-xs font-bold uppercase tracking-widest mb-4">
                        Our Arsenal
                    </span>
                    <h2 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight mb-6">
                        Technologies We <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-fuchsia-600">Master</span>
                    </h2>
                    <p className="text-slate-500 text-lg max-w-2xl mx-auto">
                        We leverage the most advanced, battle-tested modern stacks to build scalable, future-proof digital products.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-7xl mx-auto perspective-1000">
                    {techCategories.map((cat, i) => (
                        <div key={i} className="tech-card-wrapper h-full">
                            <TechCard tech={cat} index={i} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TechStackModern;
